import type { CSSProperties, ReactNode } from "react";
import { Button } from "./button";
import { Icon, type IconName } from "./icon";
import { cn } from "@/lib/cn";

/**
 * Skeletons mirror the shape of the thing they replace — never a generic
 * spinner in the middle of a card.
 */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div className={cn("animate-pulse rounded-lg bg-surface-sunken", className)} style={style} />;
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="flex flex-col">
      <div className="flex gap-4 border-b border-line px-4 py-2.5">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b border-line px-4 py-3 last:border-0">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className="h-3.5 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonChart({ height = 240 }: { height?: number }) {
  // Mirrors a column chart: a baseline with bars of varying height above it.
  const heights = [58, 82, 44, 70, 92, 51, 66];
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-end gap-3" style={{ height }}>
        {heights.map((h, i) => (
          <Skeleton key={i} className="flex-1" style={{ height: `${h}%` }} />
        ))}
      </div>
      <Skeleton className="h-3 w-1/3" />
    </div>
  );
}

/**
 * Empty states are composed: one icon, one line that says what would live here,
 * and where practical one action that populates the view.
 */
export function EmptyState({
  icon = "empty",
  title,
  body,
  action,
  className,
}: {
  icon?: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center px-6 py-12 text-center", className)}>
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface-sunken text-muted">
        <Icon name={icon} size={24} />
      </span>
      <p className="text-sm font-medium text-ink">{title}</p>
      {body && <p className="mt-1.5 max-w-[46ch] text-sm leading-relaxed text-muted">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Page-level failure. Transient action failures use a toast instead; field
 * validation renders inline under the input.
 */
export function ErrorState({
  title = "Could not load this",
  message,
  onRetry,
  className,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center px-6 py-12 text-center", className)}>
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-bad-wash text-bad">
        <Icon name="warning" size={24} />
      </span>
      <p className="text-sm font-medium text-ink">{title}</p>
      {message && <p className="mt-1.5 max-w-[46ch] text-sm leading-relaxed text-muted">{message}</p>}
      {onRetry && (
        <Button className="mt-4" size="sm" iconLeft="refresh" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * The honest state for a module that exists in the product but has no backend
 * behind it yet. Deliberately not filled with sample records: an empty module
 * is the truth, and inventing rows here would make the screen lie.
 */
export function NotConnectedState({
  module,
  what,
  needs,
}: {
  module: string;
  what: string;
  needs: string[];
}) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface-sunken text-muted">
        <Icon name="unplugged" size={24} />
      </span>
      <p className="text-sm font-medium text-ink">{module} has no data source yet</p>
      <p className="mt-1.5 max-w-[52ch] text-sm leading-relaxed text-muted">{what}</p>
      <div className="mt-5 w-full max-w-md rounded-xl border border-line bg-surface-sunken p-4 text-left">
        <p className="eyebrow mb-2">Needed before this screen can show anything</p>
        <ul className="flex flex-col gap-1.5">
          {needs.map((need) => (
            <li key={need} className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-soft">
              <Icon name="minus" size={16} className="mt-0.5 text-faint" />
              <span>{need}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
